import moment from "moment";
import { usdFormat } from "../../../utils";
import { COUNT_METRICS, MetricKey } from "../api/types";

// API money metrics are integer CENTS; usdFormat expects dollars.
export const centsToUsd = (cents?: number | null): string =>
  usdFormat.format((cents ?? 0) / 100);

export const formatCount = (n?: number | null): string =>
  (n ?? 0).toLocaleString("en-US");

// Format a metric value for display: counts as integers, everything else as USD.
export const formatMetric = (metric: MetricKey, value?: number | null): string =>
  COUNT_METRICS.has(metric) ? formatCount(value) : centsToUsd(value);

// Mirrors the old reports' UTC date rendering (e.g. "Sat, Jun 28, 2026").
export const formatDay = (value?: string | null): string => {
  if (!value) return "";
  const d = moment.utc(value);
  return d.isValid() ? d.format("ddd, MMM DD, YYYY") : value;
};

// Human labels for metric keys, matching the language used in the old reports.
// The tender labels reproduce the old Financial-family reports' column headers EXACTLY
// (QA diffs new vs old): "Gross <Tender> Sales" for $, "# of Transactions - <Tender>" for counts —
// including the old quirk of "Token" singular on the $ column and "Tokens" plural on the count.
export const METRIC_LABELS: Record<MetricKey, string> = {
  OrderCount: "Orders",
  Gross: "Gross Sales",
  Tax: "Tax",
  Tip: "Tips",
  Surcharge: "Service Fee",
  Units: "Units Sold",
  ItemRevenue: "Item Revenue",
  ItemTax: "Item Tax",
  ItemDiscount: "Item Discount",
  ItemGross: "Gross",
  TransactionCount: "Transactions",
  TransactionAmount: "Transaction Amount",
  NetSales: "Net Sales",
  Discount: "Discounts",
  Refunds: "Refunds",
  TenderCashAmount: "Gross Cash Sales",
  TenderCreditAmount: "Gross Credit Sales",
  TenderRfidAmount: "Gross RFID Sales",
  TenderCashBalanceAmount: "Gross Cash Balance Sales",
  TenderPromoBalanceAmount: "Gross Promo Balance Sales",
  TenderTokenAmount: "Gross Token Sales",
  TenderOtherAmount: "Gross Other Sales",
  TenderCashCount: "# of Transactions - Cash",
  TenderCreditCount: "# of Transactions - Credit",
  TenderRfidCount: "# of Transactions - RFID",
  TenderCashBalanceCount: "# of Transactions - Cash Balance",
  TenderPromoBalanceCount: "# of Transactions - Promo Balance",
  TenderTokenCount: "# of Transactions - Tokens",
  TenderOtherCount: "# of Transactions - Other",
  // RP-2174 decompositions (default labels; the Ticketing report overrides some to the legacy headers).
  DiscountExPromo: "Discounts",
  PromoDiscount: "Promo Codes",
  RefundsPreTax: "Refunds (pre-tax)",
  MerchantProcessed: "Merchant Processed",
  TenderCreditTip: "Tips - Credit",
  TenderCashTip: "Tips - Cash",
  TenderCashBalanceTip: "Tips - Cash Balance",
  TenderRfidTip: "Tips - RFID",
  TenderPromoBalanceTip: "Tips - Promo Balance",
  TenderTokenTip: "Tips - Tokens",
  TenderOtherTip: "Tips - Other",
  TenderUntenderedAmount: "Untendered",
  NetPromoBalanceSales: "Net Deduction - Promo Balance Sales",
};
